import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccessMode } from '../context/AccessModeContext';
import { fetchMemberNotificationSettings } from './useCenterQueryService';
import useCenterMutationService from './useCenterMutationService';
import {
    MemberNotificationSettings,
    MemberNotificationSettingsApiResult,
    MemberNotificationSettingsClientBlockReason,
    MemberNotificationSettingsPatchBody,
    MemberNotificationSettingsPhase,
    MemberNotificationSettingsResultClass,
    deriveMemberNotificationSettingsView,
    parseMemberNotificationSettings,
    sanitizeMemberNotificationSettingsPatch,
} from '../lib/memberNotificationSettings';

/**
 * 모달이 열릴 때마다 새로 받는 id. 쿼리 키 마지막 칸에 들어간다.
 * (서버 응답이 아니라 순번이다 — 모듈 스코프에 둬도 계정 전환 누수와 무관하다)
 */
let openSessionSeq = 0;
const nextOpenSessionId = () => {
    openSessionSeq += 1;
    return openSessionSeq;
};

const blocked = (reason: MemberNotificationSettingsClientBlockReason): MemberNotificationSettingsApiResult => ({
    status: 0,
    data: { clientBlocked: reason },
});

export type UseMemberNotificationSettingsResult = {
    /** 7-5 분기. `ready` 가 아니면 토글·저장 버튼을 그리지 않는다 */
    phase: MemberNotificationSettingsPhase;
    /** phase === 'loading' */
    isLoading: boolean;
    /**
     * 🔴 규칙 15 — **이번 모달 열림에서 시작한 GET 이 200 으로 끝났고, 진행 중인 GET 이 없을 때만** true.
     * 토글·저장 버튼은 이 값이 true 일 때만 그린다. 재오픈 시 이전 캐시로 먼저 true 가 되지 않는다.
     */
    isReady: boolean;
    /** isReady 일 때만 값이 있다. 같은 응답이면 참조가 유지된다(초안 초기화 effect 의 의존성으로 써도 된다) */
    settings: MemberNotificationSettings | null;
    /** 마지막으로 끝난 GET 의 HTTP 상태 (응답 없음 0). 끝난 GET 이 없으면 null */
    status: number | null;
    /** 마지막으로 끝난 GET 의 분류 */
    loadResult: MemberNotificationSettingsResultClass | null;
    /** 센터 열람 모드 — 레이아웃은 보이고 조작은 비활성, "센터 열람 모드에서는 변경할 수 없어요." */
    isReadOnly: boolean;
    /** 저장 요청 진행 중 — 버튼 로딩, 토글 비활성 */
    isSaving: boolean;
    /** isReady && !isReadOnly && !isSaving — 토글·저장 버튼 활성 여부 */
    canEdit: boolean;
    /** 다시 시도. 저장 중에는 아무것도 하지 않는다 */
    refetch: () => void;
    /**
     * 저장. 성공·실패 모두 `{ status, data }` 로 resolve 한다(던지지 않는다). 성공은 `status === 200` 만.
     *
     * 요청을 보내지 않는 경우 → `{ status: 0, data: { clientBlocked } }`
     *   READ_ONLY(열람 모드) · NOT_READY(isReady 아님) · EMPTY(보낼 키 없음) · IN_FLIGHT(저장 중 연타)
     *
     * 200 이면 응답(서버가 저장 후 다시 읽은 전체 상태)으로 캐시를 갱신한다 → `settings` 가 새 값이 된다.
     * 200 인데 응답 모양을 못 읽으면 추측하지 않고 GET 을 다시 부른다(그동안 phase 는 loading).
     * 실패면 캐시를 건드리지 않는다 — 초안은 화면이 그대로 들고 있으면 된다.
     */
    save: (body: MemberNotificationSettingsPatchBody | null) => Promise<MemberNotificationSettingsApiResult>;
};

/**
 * 회원 알림 설정 모달의 데이터 훅. **모달이 열려 있는 동안만 마운트한다**(열릴 때마다 새로 조회한다).
 *
 * 설계: rappo `docs/CENTER_MEMBER_EXPIRY_ALERT_SETTING.md` 규칙 15·16, 7-4, 7-5
 *
 * 쿼리 옵션을 전용으로 두는 이유 (설계 7-4, 4회차 교차검증 지적):
 *   `useSingleQuery` 는 `keepPreviousData: true` 이고 전역 `staleTime` 이 1분이라, 다시 열면 이전 200 캐시가
 *   `isLoading: false` 로 먼저 보인다. 그 사이 백엔드가 롤백됐으면 쓸 수 없는 설정을 편집 가능하게 보여 준다.
 *   → 열림마다 새 쿼리 키(openSessionId) + `cacheTime: 0` + `keepPreviousData: false`.
 *     `isFetchedAfterMount` 로 판정하지 않은 이유: 닫았다 바로 다시 열면 **이전 열림에서 시작한 요청**을
 *     React Query 가 그대로 이어받아(dedupe) 그 결과로 편집 가능이 될 수 있다. 키가 다르면 섞일 수 없다.
 */
export const useMemberNotificationSettings = ({
    centerId,
}: {
    centerId: string | null | undefined;
}): UseMemberNotificationSettingsResult => {
    const { isReadOnly } = useAccessMode();
    const queryClient = useQueryClient();
    const [openSessionId] = useState(nextOpenSessionId);
    const hasCenterId = typeof centerId === 'string' && centerId.length > 0;

    const { queryKey, queryFn } = fetchMemberNotificationSettings({ centerId: centerId ?? '', openSessionId });

    const query = useQuery({
        queryKey,
        queryFn,
        enabled: hasCenterId, // 🔴 전역 기본값이 enabled:false 라 명시해야 조회한다
        staleTime: 0,
        cacheTime: 0,
        keepPreviousData: false,
        retry: false,
        suspense: false,
        useErrorBoundary: false,
        refetchOnWindowFocus: false,
        // 재연결 자동 재조회를 끈다 — 편집 중에 조회가 돌면 토글이 사라졌다 나타난다
        refetchOnReconnect: false,
        // 오프라인이면 요청을 멈춰 두지 말고 보내서 네트워크 실패로 받는다(스피너 고착 방지)
        networkMode: 'always',
    });

    const { patchMemberNotificationSettingsMutation } = useCenterMutationService({});
    const mutateAsync = patchMemberNotificationSettingsMutation.mutateAsync;

    // settings 참조를 응답이 바뀔 때만 바꾼다 — 매 렌더 새 객체면 초안 초기화 effect 가 초안을 계속 덮는다
    const view = useMemo(
        () =>
            deriveMemberNotificationSettingsView({
                hasCenterId,
                queryStatus: query.status,
                isFetching: query.isFetching,
                data: query.data,
            }),
        [hasCenterId, query.status, query.isFetching, query.data],
    );

    const [isSaving, setIsSaving] = useState(false);
    const inFlightRef = useRef(false);

    // save/refetch 가 렌더 시점 클로저가 아니라 최신 상태를 보게 한다
    const latestRef = useRef({ view, isReadOnly, centerId, queryKey });
    latestRef.current = { view, isReadOnly, centerId, queryKey };

    const refetchQuery = query.refetch;
    const refetch = useCallback(() => {
        if (inFlightRef.current) return;
        if (!latestRef.current.centerId) return;
        void refetchQuery();
    }, [refetchQuery]);

    const save = useCallback(
        async (body: MemberNotificationSettingsPatchBody | null): Promise<MemberNotificationSettingsApiResult> => {
            const latest = latestRef.current;
            if (latest.isReadOnly) return blocked('READ_ONLY');
            if (latest.view.phase !== 'ready' || !latest.view.settings || !latest.centerId) {
                return blocked('NOT_READY');
            }
            const cleanBody = sanitizeMemberNotificationSettingsPatch(latest.view.settings, body);
            if (!cleanBody) return blocked('EMPTY');
            if (inFlightRef.current) return blocked('IN_FLIGHT');

            inFlightRef.current = true;
            setIsSaving(true);
            try {
                let result: MemberNotificationSettingsApiResult;
                try {
                    result = await mutateAsync({ centerId: latest.centerId, body: cleanBody });
                } catch {
                    // API 함수는 던지지 않는다. 그래도 던졌다면 응답을 못 받은 것으로 돌려준다(규칙 16)
                    result = { status: 0, data: null };
                }

                // 저장 응답 전에 모달을 닫았으면(쿼리가 이미 치워졌으면) 캐시에 새 항목을 만들지 않는다
                if (result?.status === 200 && queryClient.getQueryState(latest.queryKey)) {
                    if (parseMemberNotificationSettings(result.data)) {
                        queryClient.setQueryData(latest.queryKey, { status: 200, data: result.data });
                    } else {
                        void queryClient.refetchQueries({ queryKey: latest.queryKey, exact: true });
                    }
                }
                return result ?? { status: 0, data: null };
            } finally {
                inFlightRef.current = false;
                setIsSaving(false);
            }
        },
        [mutateAsync, queryClient],
    );

    return {
        phase: view.phase,
        isLoading: view.phase === 'loading',
        isReady: view.phase === 'ready',
        settings: view.settings,
        status: view.loadResult?.status ?? null,
        loadResult: view.loadResult,
        isReadOnly,
        isSaving,
        canEdit: view.phase === 'ready' && !isReadOnly && !isSaving,
        refetch,
        save,
    };
};

export default useMemberNotificationSettings;
